import type { SupabaseClient } from '@supabase/supabase-js'
import { indexedDbAuthStorage } from './authStorage'
import { getSupabaseConfig } from './config'
import { AUTH_STORAGE_PREFIX } from '../utils/storageNames'

let clientPromise: Promise<SupabaseClient> | null = null

export type SupabaseRealtimeHeartbeatStatus =
  | 'sent'
  | 'ok'
  | 'error'
  | 'timeout'
  | 'disconnected'
  | (string & {})

type SupabaseRealtimeHeartbeatListener = (
  status: SupabaseRealtimeHeartbeatStatus,
  latency?: number,
) => void

const realtimeHeartbeatListeners = new Set<SupabaseRealtimeHeartbeatListener>()

export function subscribeToSupabaseRealtimeHeartbeat(
  listener: SupabaseRealtimeHeartbeatListener,
): () => void {
  realtimeHeartbeatListeners.add(listener)
  return () => realtimeHeartbeatListeners.delete(listener)
}

export function reportSupabaseRealtimeHeartbeat(
  status: SupabaseRealtimeHeartbeatStatus,
  latency?: number,
): void {
  for (const listener of realtimeHeartbeatListeners) {
    try {
      listener(status, latency)
    } catch {
      // Connection-health observers must never interrupt the SDK heartbeat lifecycle.
    }
  }
}

export function getSupabaseClient(): Promise<SupabaseClient> {
  const config = getSupabaseConfig()
  if (!config) return Promise.reject(new Error('Supabase is not configured.'))

  if (config.backend === 'neon') {
    clientPromise ??= import('../neon/dataClient').then(({ getNeonDataClient }) => getNeonDataClient())
    return clientPromise
  }
  clientPromise ??= import('@supabase/supabase-js').then(({ createClient }) =>
    createClient(config.url, config.publishableKey, {
      auth: {
        autoRefreshToken: true,
        detectSessionInUrl: true,
        flowType: 'pkce',
        persistSession: true,
        storage: indexedDbAuthStorage,
        storageKey: `${AUTH_STORAGE_PREFIX}-${config.environment}`,
      },
      realtime: {
        heartbeatCallback: reportSupabaseRealtimeHeartbeat,
      },
    }),
  )
  return clientPromise
}
